import { Box, VStack, HStack, Text, Button, IconButton, useColorModeValue, useToast } from '@chakra-ui/react';
import { FiArrowLeft, FiDownload, FiShare, FiBookmark, FiSettings, FiEye, FiEyeOff, FiBook } from 'react-icons/fi';
import { useState, useEffect, useRef } from 'react';
import IconWrapper from './IconWrapper';
import { viewContent } from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';

interface BookReaderProps {
  bookId: string;
  bookTitle: string;
  bookFileUrl: string;
  onClose: () => void;
}

const BookReader: React.FC<BookReaderProps> = ({ bookId, bookTitle, bookFileUrl, onClose }) => {
  const toast = useToast();
  const { user } = useAuth();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const hasTrackedView = useRef(false);

  const bgColor = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Track view when book reader opens (only once per session)
  useEffect(() => {
    const trackView = async () => {
      if (user && bookId && !hasTrackedView.current) {
        try {
          hasTrackedView.current = true;
          await viewContent(bookId, user.uid);
        } catch (error) {
          console.error('Error tracking book reader view:', error);
          hasTrackedView.current = false; // Reset on error so it can retry
        }
      }
    };

    trackView();
  }, [user, bookId]);

  // Get file extension to determine reader type
  const getFileExtension = (url: string): string => {
    // Remove query parameters first, then get extension
    const urlWithoutParams = url.split('?')[0];
    return urlWithoutParams.split('.').pop()?.toLowerCase() || '';
  };

  const fileExtension = getFileExtension(bookFileUrl);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle download
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = bookFileUrl;
    link.download = `${bookTitle}.${fileExtension}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: 'Download started',
      description: 'Your book download has started',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle share
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: bookTitle,
          text: `Check out this book: ${bookTitle}`,
          url: window.location.href,
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link copied',
        description: 'Book link copied to clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle bookmark
  const handleBookmark = () => {
    toast({
      title: 'Bookmark added',
      description: 'Book has been bookmarked',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Render appropriate reader based on file type
  const renderReader = () => {
    if (!bookFileUrl) {
      return (
        <VStack gap={4} py={8}>
          <Text fontSize="lg" color={textColor}>Book file not available</Text>
          <Text color="gray.500">This book doesn't have a downloadable file yet.</Text>
        </VStack>
      );
    }

    switch (fileExtension) {
      case 'pdf':
        return (
          <Box w="100%" h="100%" position="relative">
            <iframe
              src={bookFileUrl}
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: '600px' }}
              onLoad={() => {
                setIsLoading(false);
              }}
              onError={() => {
                setIsLoading(false);
                // Fallback to Google Docs viewer for mobile compatibility
                const iframe = document.querySelector('iframe');
                if (iframe) {
                  iframe.src = `https://docs.google.com/gview?url=${encodeURIComponent(bookFileUrl)}&embedded=true`;
                }
              }}
              title={bookTitle}
              allow="fullscreen"
            />
            {/* Fallback message for mobile if iframe doesn't load */}
            {!isLoading && (
              <Box
                position="absolute"
                bottom={4}
                left="50%"
                transform="translateX(-50%)"
                bg="blackAlpha.700"
                color="white"
                px={4}
                py={2}
                borderRadius="md"
                fontSize="sm"
                display={{ base: 'block', md: 'none' }}
              >
                If the PDF doesn't load, try downloading it
              </Box>
            )}
          </Box>
        );
      
      case 'epub':
        return (
          <VStack gap={6} py={12} px={8} textAlign="center">
            <Box
              w="120px"
              h="120px"
              borderRadius="full"
              bg="purple.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <IconWrapper icon={FiBook} size={48} color="purple.500" />
            </Box>
            <VStack gap={3}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>EPUB Book</Text>
              <Text fontSize="lg" color="gray.500" maxW="400px">
                EPUB files cannot be displayed directly in web browsers. 
                Download the file to read it with your preferred e-book reader.
              </Text>
            </VStack>
            <VStack gap={3}>
              <Button 
                colorScheme="purple" 
                size="lg"
                onClick={handleDownload}
                leftIcon={<IconWrapper icon={FiDownload} size={20} />}
              >
                Download EPUB File
              </Button>
              <Text fontSize="sm" color="gray.400">
                Recommended apps: Apple Books, Adobe Digital Editions, Calibre
              </Text>
            </VStack>
          </VStack>
        );
      
      case 'mobi':
        return (
          <VStack gap={6} py={12} px={8} textAlign="center">
            <Box
              w="120px"
              h="120px"
              borderRadius="full"
              bg="blue.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <IconWrapper icon={FiBook} size={48} color="blue.500" />
            </Box>
            <VStack gap={3}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>MOBI Book</Text>
              <Text fontSize="lg" color="gray.500" maxW="400px">
                MOBI files cannot be displayed directly in web browsers. 
                Download the file to read it with your preferred e-book reader.
              </Text>
            </VStack>
            <VStack gap={3}>
              <Button 
                colorScheme="blue" 
                size="lg"
                onClick={handleDownload}
                leftIcon={<IconWrapper icon={FiDownload} size={20} />}
              >
                Download MOBI File
              </Button>
              <Text fontSize="sm" color="gray.400">
                Recommended apps: Kindle app, Calibre, FBReader
              </Text>
            </VStack>
          </VStack>
        );
      
      default:
        return (
          <VStack gap={4} py={8}>
            <Text fontSize="lg" color={textColor}>Unsupported Format</Text>
            <Text color="gray.500">This file format is not supported for online reading.</Text>
            <Button colorScheme="purple" onClick={handleDownload}>
              <IconWrapper icon={FiDownload} size={16} style={{ marginRight: '8px' }} />
              Download to Read
            </Button>
          </VStack>
        );
    }
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg={bgColor}
      zIndex={1000}
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      {showControls && (
        <Box
          bg={bgColor}
          borderBottom="1px solid"
          borderColor={borderColor}
          p={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack gap={4}>
            <IconButton
              aria-label="Close reader"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              variant="ghost"
              onClick={onClose}
            />
            <VStack align="start" gap={0}>
              <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1}>
                {bookTitle}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {fileExtension.toUpperCase()} Reader
              </Text>
            </VStack>
          </HStack>

          <HStack gap={2}>
            <IconButton
              aria-label="Download"
              icon={<IconWrapper icon={FiDownload} size={20} />}
              variant="ghost"
              onClick={handleDownload}
              title="Download book"
            />
            <IconButton
              aria-label="Share"
              icon={<IconWrapper icon={FiShare} size={20} />}
              variant="ghost"
              onClick={handleShare}
              title="Share book"
            />
            <IconButton
              aria-label="Bookmark"
              icon={<IconWrapper icon={FiBookmark} size={20} />}
              variant="ghost"
              onClick={handleBookmark}
              title="Bookmark"
            />
            <IconButton
              aria-label="Toggle fullscreen"
              icon={<IconWrapper icon={isFullscreen ? FiEyeOff : FiEye} size={20} />}
              variant="ghost"
              onClick={toggleFullscreen}
              title="Toggle fullscreen"
            />
            <IconButton
              aria-label="Toggle controls"
              icon={<IconWrapper icon={FiSettings} size={20} />}
              variant="ghost"
              onClick={() => setShowControls(!showControls)}
              title="Toggle controls"
            />
          </HStack>
        </Box>
      )}

      {/* Reader Content */}
      <Box flex="1" position="relative">
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={bgColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
          >
            <VStack gap={4}>
              <Text color={textColor}>Loading book...</Text>
            </VStack>
          </Box>
        )}
        
        {renderReader()}
      </Box>

      {/* Click to show controls */}
      {!showControls && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          onClick={() => setShowControls(true)}
          cursor="pointer"
        />
      )}
    </Box>
  );
};

export default BookReader;
